import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import HubspotForm from '../../shared/hubspot-form';
import StepsBar from './StepsBar';
import styles from './styles.module.scss';
import Heading from '../../shared/heading';
import Button from '../../shared/button';

const SCRIPT_URL = 'https://app.revenuehero.io/scheduler.min.js';

const BookMeeting = ({
  contactFormId,
  revenueheroRouterId,
  additionalDetailsFormId,
  title = null,
  contactFormDescription = null,
  additionalDetailsFormDescription = null,
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const [contactData, setContactData] = useState([]);
  const [showOpenBookingModalButton, setShowOpenBookingModalButton] = useState(false);
  const [isHubspotLoaded, setIsHubspotLoaded] = useState(false);
  const [isRHLoaded, setIsRHLoaded] = useState(false);
  const [isContactFormSubmitted, setIsContactFormSubmitted] = useState(false);
  const [isAdditionalDetailsFormSubmitted, setIsAdditionalDetailsFormSubmitted] = useState(false);
  const [isMeetingScheduled, setIsMeetingScheduled] = useState(false);
  const closeBookingModalRef = useRef();
  const isMeetingScheduledRef = useRef(false);
  const revenueHeroRef = useRef(null);

  const openBookingModal = useCallback(() => {
    // eslint-disable-next-line no-undef
    revenueHeroRef.current = new RevenueHero({
      routerId: revenueheroRouterId,
    });
    // hero.dialog.setEmbedTarget('#embedDiv');
    const email = contactData.find((field) => field.name === 'email')?.value;
    const firstname = contactData.find((field) => field.name === 'firstname')?.value;
    const lastname = contactData.find((field) => field.name === 'lastname')?.value;
    const phone = contactData.find((field) => field.name === 'phone')?.value;

    revenueHeroRef.current
      .submit({
        email,
        firstname,
        lastname,
        phone,
      })
      .then((data) => {
        revenueHeroRef.current.dialog.open(data);
      })
      .catch((error) => {
        window?.console.error(error);
      });
  }, [revenueheroRouterId, contactData]);

  useEffect(() => {
    if (
      isHubspotLoaded &&
      isRHLoaded &&
      window.RevenueHero &&
      revenueheroRouterId &&
      contactFormId
    ) {
      // eslint-disable-next-line no-undef
      revenueHeroRef.current = new RevenueHero({ routerId: revenueheroRouterId });
      // window.hero.dialog.setEmbedTarget('#embedDiv');
      revenueHeroRef.current.schedule(`hsForm_${contactFormId}`);
    }
  }, [isHubspotLoaded, isRHLoaded, revenueheroRouterId, contactFormId]);

  useEffect(() => {
    // Check if the script already exists in the document
    const existingScript = document.querySelector(`script[src="${SCRIPT_URL}"]`);

    if (existingScript) {
      // If the script already exists, set it as loaded
      setIsRHLoaded(true);
    } else {
      // Create a script element if not already loaded
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;

      // Set script as loaded once it finishes loading
      script.onload = () => {
        setIsRHLoaded(true);
      };

      // Append the script to the body
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', (ev) => {
      if (ev.data.type === 'MEETING_BOOKED' || ev.data.type === 'MEETING_RESCHEDULED') {
        isMeetingScheduledRef.current = true;
        setActiveStep(3);
        window?.scrollTo(0, 0);
        setIsMeetingScheduled(true);
        closeBookingModalRef.current = setTimeout(() => {
          revenueHeroRef.current?.dialog?.close(); // Close the modal
          setShowOpenBookingModalButton(false);
        }, 4000);
      }

      if (ev.data.type === 'CLOSE_DIALOG') {
        if (!isMeetingScheduledRef.current) {
          setShowOpenBookingModalButton(true);
        }
      }

      if (ev.data.type === 'hsFormCallback' && ev.data.eventName === 'onFormSubmit') {
        const formFields = ev.data.data;
        setContactData(formFields);
        window?.scrollTo(0, 0);
      }
    });
  }, []);

  const steps = useMemo(
    () => [
      { label: '1', description: 'Contact Info' },
      { label: '2', description: 'Pick a meeting time' },
      { label: '3', description: 'Additional Details' },
    ],
    []
  );

  const onContactFormReady = useCallback(() => {
    setIsHubspotLoaded(true);
  }, []);

  const onContactFormSubmitted = useCallback(() => {
    setActiveStep(2);
    setIsContactFormSubmitted(true);
    setShowOpenBookingModalButton(true);
  }, []);

  const onAdditionalDetailsFormReady = useCallback(
    (form) => {
      contactData?.forEach(({ name, value }) => {
        const field = form.querySelector(`input[name="${name}"]`);
        if (field) {
          field.value = value;
          field.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    },
    [contactData]
  );

  const onAdditionalDetailsFormSubmitted = useCallback(() => {
    setIsAdditionalDetailsFormSubmitted(true);
  }, []);

  if (!revenueheroRouterId || !contactFormId) {
    return null;
  }

  return (
    <div className="container">
      <section
        className={cn(styles.root, {
          'contact-form-submitted': isContactFormSubmitted,
          'meeting-scheduled': isMeetingScheduled,
          'additional-details-submitted': isAdditionalDetailsFormSubmitted,
        })}
      >
        {title && (
          <Heading size="xl" className={styles.title}>
            {title}
          </Heading>
        )}
        <div className={styles.stepsWrapper}>
          <StepsBar steps={steps} currentStep={activeStep} />
        </div>
        {contactFormId && activeStep === 1 && (
          <div className={styles.formWrapper}>
            {contactFormDescription && (
              <Heading size="md" className={styles.subTitle}>
                {contactFormDescription}
              </Heading>
            )}
            <HubspotForm
              formId={contactFormId}
              targetId={contactFormId}
              onFormReady={onContactFormReady}
              onFormSubmitted={onContactFormSubmitted}
            />
          </div>
        )}
        {activeStep === 2 && showOpenBookingModalButton && (
          <div className={styles.openModalButton}>
            <Button theme="accent-primary" onClick={openBookingModal}>
              Pick a meeting time
            </Button>
          </div>
        )}
        {activeStep === 3 && (
          <div className={styles.formWrapper}>
            {additionalDetailsFormDescription && (
              <Heading size="lg" className={styles.title}>
                {additionalDetailsFormDescription}
              </Heading>
            )}
            {additionalDetailsFormId && (
              <HubspotForm
                formId={additionalDetailsFormId}
                targetId={additionalDetailsFormId}
                onFormReady={onAdditionalDetailsFormReady}
                onFormSubmitted={onAdditionalDetailsFormSubmitted}
              />
            )}
          </div>
        )}
      </section>
    </div>
  );
};

BookMeeting.propTypes = {
  contactFormId: PropTypes.string.isRequired,
  revenueheroRouterId: PropTypes.number.isRequired,
  additionalDetailsFormId: PropTypes.string.isRequired,
  title: PropTypes.string,
  contactFormDescription: PropTypes.string,
  additionalDetailsFormDescription: PropTypes.string,
};

export default BookMeeting;
